import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, MessageCircle, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { IONA, waLink } from "@/constants/site";

const NAV = [
  { label: "Anasayfa", href: "/#hero", id: "anasayfa" },
  { label: "Hizmetler", href: "/#hizmetler", id: "hizmetler" },
  { label: "Kampanyalar", href: "/#kampanyalar", id: "kampanyalar" },
  { label: "Neden İONA?", href: "/#neden-iona", id: "neden-iona" },
  { label: "Blog", href: "/blog", id: "blog" },
  { label: "İletişim", href: "/#iletisim", id: "iletisim" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (href) => {
    setOpen(false);
    if (href.startsWith("/#")) {
      const id = href.replace("/#", "");
      if (location.pathname !== "/") {
        navigate("/");
        setTimeout(() => {
          document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
        }, 80);
      } else {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      navigate(href);
    }
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/90 backdrop-blur-lg border-b border-[#E5E5E5] shadow-[0_2px_20px_rgba(94,42,132,0.06)]"
          : "bg-transparent"
      }`}
    >
      <div className="iona-container flex items-center justify-between h-20">
        <Link
          to="/"
          data-testid="navbar-logo"
          className="flex items-center gap-3"
        >
          <div className="w-[58px] h-[58px] md:w-14 md:h-14 rounded-full bg-[#5E2A84] flex items-center justify-center overflow-hidden">
            <img
              src={IONA.logo}
              alt="İONA"
              className="w-[42px] md:w-10 h-auto object-contain"
            />
          </div>
          <div className="hidden sm:flex flex-col leading-tight">
            <span className="font-serif text-xl text-[#1A1A1A] tracking-wide">
              İONA
            </span>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {NAV.map((item) => (
            <button
              key={item.id}
              data-testid={`nav-link-${item.id}`}
              onClick={() => handleNav(item.href)}
              className="text-sm text-[#4A4A4A] hover:text-[#5E2A84] transition-colors relative group"
            >
              {item.label}
              <span className="absolute -bottom-1 left-0 w-0 h-px bg-[#5E2A84] group-hover:w-full transition-all duration-300" />
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          <a
            href={`tel:${IONA.phoneIntl}`}
            data-testid="navbar-call-cta"
            className="inline-flex"
          >
            <Button
              variant="outline"
              className="rounded-full h-11 w-11 sm:w-auto sm:px-5 border-[#5E2A84]/25 text-[#5E2A84] hover:bg-[#F4F1F9] hover:text-[#5E2A84] flex items-center justify-center"
              aria-label="Hemen Ara"
            >
              <Phone className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline text-sm font-medium">
                Hemen Ara
              </span>
            </Button>
          </a>

          {/* Mobile-only WhatsApp icon button (between phone and menu) */}
          <a
            href={waLink()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="navbar-whatsapp-mobile"
            className="md:hidden inline-flex"
            aria-label="WhatsApp'tan Yaz"
          >
            <span className="h-11 w-11 rounded-full border border-[#5E2A84]/25 bg-white hover:bg-[#F4F1F9] flex items-center justify-center transition-colors">
              <MessageCircle className="w-5 h-5 text-[#25D366]" strokeWidth={2} />
            </span>
          </a>

          {/* Desktop WhatsApp pill button */}
          <a
            href={waLink()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="navbar-whatsapp-cta"
            className="hidden md:inline-flex"
          >
            <Button className="bg-[#5E2A84] hover:bg-[#4a2168] text-white rounded-full h-11 px-6 text-sm font-medium iona-soft-shadow">
              <MessageCircle className="w-4 h-4 mr-2" />
              WhatsApp'tan Randevu Al
            </Button>
          </a>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="mobile-menu-trigger"
                className="lg:hidden w-11 h-11 rounded-full border border-[#E5E5E5] flex items-center justify-center text-[#5E2A84]"
                aria-label="Menü"
              >
                {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-white w-[300px] sm:w-[360px]">
              <div className="flex flex-col gap-1 mt-10">
                {NAV.map((item) => (
                  <button
                    key={item.id}
                    data-testid={`mobile-nav-${item.id}`}
                    onClick={() => handleNav(item.href)}
                    className="text-left py-4 px-2 border-b border-[#F0F0F0] text-[#1A1A1A] hover:text-[#5E2A84] font-serif text-lg"
                  >
                    {item.label}
                  </button>
                ))}
                <a
                  href={waLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="mobile-whatsapp-cta"
                  className="mt-6"
                >
                  <Button className="w-full bg-[#5E2A84] hover:bg-[#4a2168] text-white rounded-full h-12">
                    <MessageCircle className="w-4 h-4 mr-2" />
                    WhatsApp'tan Randevu Al
                  </Button>
                </a>
                <a
                  href={`tel:${IONA.phoneIntl}`}
                  data-testid="mobile-call-cta"
                  className="mt-3"
                >
                  <Button
                    variant="outline"
                    className="w-full rounded-full h-12 border-[#5E2A84]/25 text-[#5E2A84] hover:bg-[#F4F1F9] hover:text-[#5E2A84]"
                  >
                    <Phone className="w-4 h-4 mr-2" />
                    Hemen Ara · {IONA.phone}
                  </Button>
                </a>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
